import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import { PlusIcon } from './icons';
import requestsService, { Request } from '../services/requestsService';
import CreateRequestModal from './CreateRequestModal';
import SettingsPage from './SettingsPage';
import TasksPage from './TasksPage';
import MembersPage from './MembersPage';
import HistoryPage from './HistoryPage';
import authService from '../services/authService';
import './Dashboard.css';

interface DashboardProps {
  onLogout: () => void;
}

interface CurrentUser {
  id: number;
  email: string;
  username: string;
  name: string; // firstName из API
  surname: string; // lastName из API
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  // Определяем активную страницу на основе текущего пути
  const getActivePage = () => {
    if (location.pathname.startsWith('/task/')) {
      return 'tasks';
    }
    return currentPage;
  };

  const activePage = getActivePage();

  // Получение информации о текущем пользователе
  const fetchCurrentUser = async () => {
    try {
      console.log('Dashboard - Fetching current user...');
      const userData = await authService.getMe();
      setCurrentUser(userData);
      console.log('Dashboard - Current user data received:', userData);
      console.log('Dashboard - User role:', userData.role);
      console.log('Dashboard - User name:', userData.name, userData.surname);
    } catch (error) {
      console.error('Dashboard - Error fetching current user:', error);
    }
  };

  useEffect(() => {
    if (currentPage === 'home') {
      fetchRequests();
    }
  }, [currentPage]);

  // Загружаем информацию о пользователе при монтировании компонента
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Проверяем токен перед запросом
      const token = authService.getAccessToken();
      console.log('Dashboard - Access token:', token);
      console.log('Dashboard - Is authenticated:', authService.isAuthenticated());
      console.log('Dashboard - Token exists:', !!token);
      console.log('Dashboard - Token length:', token ? token.length : 0);
      
      const response = await requestsService.getRequests();
      setRequests(response.content);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
  };

  const handleAddApplication = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    // Обновляем список заявок после создания новой
    fetchRequests();
  };

  const handleSeeAll = () => {
    console.log('See all clicked');
  };

  const handlePageChange = (page: string) => {
    // Проверяем права доступа для определенных страниц
    if (page === 'members' || page === 'history' || page === 'settings') {
      if (currentUser?.role !== 'ADMIN') {
        console.log('Access denied: User role is not ADMIN');
        alert('У вас нет прав доступа к этой странице. Требуется роль ADMIN.');
        return; // Не переключаем страницу, если у пользователя нет прав
      }
    }
    
    setCurrentPage(page);
    
    switch (page) {
      case 'home':
        // Остаемся на dashboard, показываем главную страницу
        break;
      case 'tasks':
        // Остаемся на dashboard, показываем TasksPage
        break;
      case 'settings':
        // Остаемся на dashboard, показываем SettingsPage
        break;
      case 'members':
        // Пока что просто логируем
        console.log('Members page clicked');
        break;
      case 'history':
        // Пока что просто логируем
        console.log('History page clicked');
        break;
      default:
        setCurrentPage('home');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return '#ef4444';
      case 'MEDIUM':
        return '#f59e0b';
      case 'LOW':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'Высокий';
      case 'MEDIUM':
        return 'Средний';
      case 'LOW':
        return 'Низкий';
      default:
        return priority;
    }
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case 'home':
        return (
          <>
            <div className="content-header">
              <h1 className="page-title">Назначенные заявки</h1>
              <button onClick={handleAddApplication} className="add-button">
                <PlusIcon className="add-icon" size={24} />
              </button>
            </div>

            {loading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Загрузка заявок...</p>
              </div>
            )}

            {error && (
              <div className="error-container">
                <p className="error-message">{error}</p>
                <button onClick={fetchRequests} className="retry-button">
                  Попробовать снова
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                <div className="applications-list">
                  {requests.map((request) => (
                    <div key={request.id} className="application-card">
                      <div className="card-left">
                        <div className="app-code">{request.title}</div>
                        <div className="driver-info">
                          Ответственный: {request.responsible.name} {request.responsible.surname}
                        </div>
                        <div className="route-info">
                          Рейс: {request.flight.flightNumber} ({request.flight.departureAirport} → {request.flight.arrivalAirport})
                        </div>
                        <div className="date-info">
                          Создано: {formatDateTime(request.createdAt)}
                        </div>
                        <div className="priority-info">
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(request.priority) }}
                          >
                            {getPriorityText(request.priority)}
                          </span>
                        </div>
                      </div>
                      <div className="card-right">
                        <div className="machine-info">
                          Машина: {request.car.name} {request.car.model}
                        </div>
                        <div className="status-info">
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: request.status.color }}
                          >
                            {request.status.name}
                          </span>
                        </div>
                        <div className="part-info">
                          Части: {request.aircraftParts && request.aircraftParts.length > 0 
                            ? request.aircraftParts.map(part => part.aircraftPartName).join(', ')
                            : 'Не указаны'
                          }
                        </div>
                        <div className="creator-info">
                          Создатель: {request.creator.name} {request.creator.surname}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {requests.length === 0 && (
                  <div className="empty-state">
                    <p>Заявки не найдены</p>
                  </div>
                )}

                <div className="see-all-section">
                  <button onClick={handleSeeAll} className="see-all-button">
                    Видеть все
                  </button>
                </div>
              </>
            )}

            <CreateRequestModal
              isOpen={isModalOpen}
              onClose={handleModalClose}
              onSuccess={handleModalSuccess}
            />
          </>
        );
      case 'tasks':
        return <TasksPage />;
      case 'settings':
        // Проверяем права доступа
        if (currentUser?.role !== 'ADMIN') {
          return (
            <div className="access-denied">
              <h2>Доступ запрещен</h2>
              <p>У вас нет прав доступа к настройкам. Требуется роль ADMIN.</p>
            </div>
          );
        }
        return <SettingsPage />;
      case 'members':
        // Проверяем права доступа
        if (currentUser?.role !== 'ADMIN') {
          return (
            <div className="access-denied">
              <h2>Доступ запрещен</h2>
              <p>У вас нет прав доступа к участникам. Требуется роль ADMIN.</p>
            </div>
          );
        }
        return <MembersPage currentUser={currentUser} />;
      case 'history':
        // Проверяем права доступа
        if (currentUser?.role !== 'ADMIN') {
          return (
            <div className="access-denied">
              <h2>Доступ запрещен</h2>
              <p>У вас нет прав доступа к истории. Требуется роль ADMIN.</p>
            </div>
          );
        }
        return <HistoryPage currentUser={currentUser} />;
      default:
        return (
          <div className="content-header">
            <h1 className="page-title">Главная</h1>
            <p>Добро пожаловать в систему управления задачами</p>
          </div>
        );
    }
  };

  return (
    <div className="dashboard">
      <Header 
        activePage={currentPage} 
        onPageChange={handlePageChange} 
        onLogout={handleLogout}
        currentUser={currentUser}
      />
      
      <main className="main-content">
        {renderPageContent()}
      </main>
    </div>
  );
};

export default Dashboard; 